import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Card, mergeClasses } from '@fluentui/react-components'
import {
    AlbumFilled,
    AlbumRegular,
    bundleIcon,
    ClipboardTaskFilled,
    ClipboardTaskRegular,
    DocumentMultipleFilled,
    DocumentMultipleRegular,
    MoneyFilled,
    MoneyRegular,
} from '@fluentui/react-icons'
import { generatePath } from 'react-router'
import OverflowToolbarPanel, {
    HammockToolbarButtonData,
    HammockToolbarProps,
    useOverflowToolbarPanelStyles,
} from '../../../components/Overflow/OverflowToolbarPanel'
import { usePageStyles } from '../File/ViewFile'
import { crmRoutePrefix, ViewCRMProps } from './ViewCRM'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'

export enum CRMTabKey {
    Files = 'file',
    Documents = 'document',
    Tasks = 'task',
    Finance = 'finance',
}

const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)
const ClipboardTaskIcon = bundleIcon(ClipboardTaskFilled, ClipboardTaskRegular)
const DocumentIcon = bundleIcon(DocumentMultipleFilled, DocumentMultipleRegular)
const MoneyIcon = bundleIcon(MoneyFilled, MoneyRegular)

export default function ViewCRMTabs(props: ViewCRMProps): ReactElement {
    const history = useHistory()
    const modules = useModules()
    const pageStyles = usePageStyles()
    const toolbarStyles = useOverflowToolbarPanelStyles()

    const activeButton = useMemo<CRMTabKey>(() => {
        return props.routeMatch.params?.tab || CRMTabKey.Files
    }, [props.routeMatch])

    const onSelectButton = (value?: string) => {
        const button = buttons.find(b => b.value === value)
        if (button?.route) {
            history.push(generatePath(crmRoutePrefix + button.route, props.routeMatch.params))
        }
    }

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            className: pageStyles.toolbar,
        }),
        [],
    )

    const buttons = useMemo<HammockToolbarButtonData[]>(() => {
        const items: HammockToolbarButtonData[] = [
            {
                value: CRMTabKey.Files,
                icon: <AlbumIcon />,
                titleKey: 'crm.tab.files',
                route: `/${CRMTabKey.Files}`,
                onClick: onSelectButton,
            },
            {
                value: CRMTabKey.Documents,
                icon: <DocumentIcon />,
                titleKey: 'crm.tab.documents',
                route: `/${CRMTabKey.Documents}`,
                onClick: onSelectButton,
            },
        ]

        if (modules.has(ModuleType.Tasks)) {
            items.push({
                value: CRMTabKey.Tasks,
                icon: <ClipboardTaskIcon />,
                titleKey: 'crm.tab.tasks',
                route: `/${CRMTabKey.Tasks}`,
                onClick: onSelectButton,
            })
        }

        if (modules.any([ModuleType.Accounting, ModuleType.Invoices])) {
            items.push({
                value: CRMTabKey.Finance,
                icon: <MoneyIcon />,
                titleKey: 'crm.tab.finance',
                route: `/${CRMTabKey.Finance}`,
                onClick: onSelectButton,
            })
        }

        return items
    }, [modules.loaded])

    return (
        <Card className={pageStyles.toolbarColor}>
            <OverflowToolbarPanel
                containerClassName={mergeClasses(pageStyles.toolbarTabs, pageStyles.toolbarColor)}
                toolbarProps={toolbarProps}
                getToolbarButtonProps={button => ({
                    appearance: activeButton === button?.value ? 'primary' : 'subtle',
                    disabled: button?.disabled,
                })}
                getMenuItemProps={button => ({
                    className: activeButton === button?.value && toolbarStyles.selectedMenuItem,
                    disabled: button?.disabled,
                })}
                buttons={buttons}
                overflowButtonLabelKey={'base.item.label'}
            />
        </Card>
    )
}
